import type {
  IntrinsicSystemProps,
  ButtonProps as SystemButtonProps,
  LinkProps as SystemLinkProps,
} from '@hover/blueprint';
import { Button, Link as SystemLink, forwardRef } from '@hover/blueprint';
import type { ComponentWithAs } from '@hover/blueprint/chakra';
import { captureException } from '@sentry/react';
import type { MouseEventHandler } from 'react';
import React from 'react';
import type { LinkProps as RouterLinkProps } from 'react-router-dom';
import {
  Link as RouterLink,
  useLocation as useRouterLocation,
} from 'react-router-dom';
import invariant from 'tiny-invariant';

import { useCurrentPage, useLocation, useMode } from 'src/shared/hooks';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import type { LinkPressed } from 'src/shared/telemetry';
import { linkPressed } from 'src/shared/telemetry';
import { isDevelopment } from 'src/shared/utils/environment';

type TrackHandler = (
  properties: LinkPressed & { button_or_link: string; button_text: string },
) => void;

/**
 * Common link props, including the `react-router` API, that
 * are shared between standard links and button links
 */
interface BaseLinkProps extends Omit<RouterLinkProps, 'children' | 'color'> {
  linkLocation?: string;
  pageName?: string;
  preserveQuery?: boolean;
  primaryCallToAction?: boolean;
  track?: TrackHandler;
  optionalTrackingProperties?: Partial<LinkPressed>;
  isExternal?: boolean;
}

/**
 * Standard link props (common props from above + design system link props)
 */
interface LinkLinkProps extends BaseLinkProps, SystemLinkProps {
  element?: 'a' | never;
}

type SystemButtonProp = 'color' | 'fill';
type SystemLinkButtonProps = Omit<
  SystemButtonProps,
  Exclude<keyof IntrinsicSystemProps<'button'>, SystemButtonProp> | 'type'
> &
  Omit<IntrinsicSystemProps<'a'>, SystemButtonProp>;

/**
 * Button link props (common props from above + design system button props...
 * with some munging related to using `as="a"` on a system button)
 */
interface ButtonLinkProps extends BaseLinkProps, SystemLinkButtonProps {
  element: 'button';
}

/**
 * Props that `react-router` passes to the custom link
 * component supplied as `component={InnerLink}`
 */
interface BaseInnerLinkProps {
  navigate?: () => void;
}

interface InnerButtonLinkProps
  extends BaseInnerLinkProps,
    Omit<ButtonLinkProps, 'to'> {}

interface InnerLinkLinkProps
  extends BaseInnerLinkProps,
    Omit<LinkLinkProps, 'to'> {}

type InnerLinkProps = InnerLinkLinkProps | InnerButtonLinkProps;

export type LinkProps = LinkLinkProps | ButtonLinkProps;

// More shenanigans related to `as="a"` on a system button
const SystemButton = Button as ComponentWithAs<'a', SystemLinkButtonProps>;

/**
 * Custom link component to be passed as the `component` prop to React Router's
 * link. This is necessary in order to make a tracking request on navigation.
 */
const InnerLink = forwardRef<InnerLinkProps, 'a'>(
  (
    {
      children,
      href,
      navigate,
      pageName,
      primaryCallToAction = false,
      track,
      linkLocation,
      optionalTrackingProperties = {},
      isExternal,
      target,
      ...props
    },
    ref,
  ) => {
    const currentPage = useCurrentPage();
    const page = pageName || currentPage;
    let linkText = props['aria-label'];

    if (typeof children === 'string') {
      linkText = children;
    } else if (
      Array.isArray(children) &&
      children.length === 1 &&
      typeof children[0] === 'string'
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      [linkText] = children;
    }

    const mode = useMode();

    const {
      metadata: { partner_id: partnerId },
    } = useTrackingMetadata();

    const onLinkClick: MouseEventHandler<HTMLAnchorElement> = event => {
      event.preventDefault();
      props.onClick?.(event);

      try {
        invariant(page, 'Link: pageName is required when current page is null');
        invariant(
          linkText,
          'Link: label is required when children is not string or an array of a single string',
        );

        const properties = {
          page_or_screen_name: page,
          primary_cta: primaryCallToAction,
          link_location: linkLocation,
          link_text: linkText,
          design_studio_mode: mode,
          partner_id: partnerId,
          ...optionalTrackingProperties,
        };

        if (track) {
          track({
            button_or_link: 'link',
            button_text: linkText,
            ...properties,
          });
        } else {
          linkPressed(properties);
        }
      } catch (error) {
        if (isDevelopment()) {
          throw new Error(
            `Failed to track. Missing tracking property \`${
              page ? 'linkText' : 'page'
            }.\``,
            {
              cause: error,
            },
          );
        }

        captureException(error);
      }

      if (navigate) {
        navigate();
      } else if (href) {
        window.open(href, target || (isExternal ? '_blank' : '_self'));
      }
    };

    const commonProps = {
      href,
      onClick: onLinkClick,
      target,
      rel: 'noopener noreferrer',
    } satisfies Partial<LinkProps>;

    return props.element === 'button' ? (
      <SystemButton
        textDecoration="none"
        {...props}
        {...commonProps}
        as="a"
        ref={ref}
        type={undefined}
      >
        {children}
      </SystemButton>
    ) : (
      <SystemLink {...props} {...commonProps} ref={ref}>
        {children}
      </SystemLink>
    );
  },
);

const Link = forwardRef<LinkProps, 'a'>(
  ({ preserveQuery, isExternal, to, ...props }, ref) => {
    const { search } = useLocation();
    const location = useRouterLocation();

    const buildToWithQuery = () => {
      if (typeof to === 'string') {
        return to + search;
      }

      if (typeof to === 'function') {
        const descriptor = to(location);

        return typeof descriptor === 'string'
          ? descriptor + search
          : { ...descriptor, search };
      }

      return { ...to, search };
    };

    const href = preserveQuery ? buildToWithQuery() : to;

    if (isExternal) {
      return (
        <InnerLink {...props} href={href as string} isExternal ref={ref} />
      );
    }

    return (
      <RouterLink
        {...props}
        // React Router passes all props to the `InnerLink`, but its type
        // definitions do not merge the inner link component's props. Instead it
        // simply expects props for an anchor tag. The anchor tag's `color` prop
        // conflicts with Blueprint's `color` prop.
        color={props.color as string}
        component={InnerLink}
        ref={ref}
        to={href}
      />
    );
  },
);

const ButtonLink = forwardRef<Omit<ButtonLinkProps, 'element'>, 'a'>(
  (props, ref) => <Link {...props} element="button" ref={ref} />,
);

export { ButtonLink, Link };
